import { useState, useEffect } from "react";
import { toast } from "sonner";
import { User, Mail, Shield, Layers, Lock, Loader2, Building2, Server, Warehouse, Factory, Clock } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  getMyProfile, updateMyProfile, changeMyPassword,
  getSmartewmUserContext, updateSmartewmUserContext, getMySmartewmContext,
} from "../lib/adminApi";

const ROLE_LABELS = { admin: "Admin", consultant: "Consultant", demo: "Demo", client: "Client" };

function ReadonlyRow({ icon: Icon, label, value, testId }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="w-4 h-4 text-[#64748B] mt-0.5 shrink-0" strokeWidth={1.5} />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold">{label}</p>
        <p className="text-sm text-[#1F2937] break-words" data-testid={testId}>{value || "—"}</p>
      </div>
    </div>
  );
}

/**
 * Shared "My Profile" modal for any logged-in user. Lets the user view their
 * profile, edit their name, and change their password. Email / role /
 * application access are read-only (admin-managed).
 */
export default function ProfileDialog({ open, onOpenChange, user, onProfileUpdated }) {
  const [profile, setProfile] = useState(user || null);
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwError, setPwError] = useState("");
  const [changingPw, setChangingPw] = useState(false);

  // Refresh profile from the server whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    let active = true;
    const load = async () => {
      setPwError(""); setCurPw(""); setNewPw(""); setConfirmPw("");
      try {
        const fresh = await getMyProfile();
        if (!active) return;
        setProfile(fresh);
        setName(fresh.name || "");
      } catch {
        if (!active || !user) return;
        setProfile(user);
        setName(user.name || "");
      }
    };
    load();
    return () => { active = false; };
  }, [open, user]);

  const p = profile || user || {};
  const roleLabel = ROLE_LABELS[(p.role || "").toLowerCase()] || (p.role || "—");
  const appAccess = Array.isArray(p.applications) && p.applications.length
    ? p.applications.join(", ")
    : (p.application_access || "—");
  const nameChanged = (name || "").trim() && (name || "").trim() !== (p.name || "");

  const saveName = async () => {
    const trimmed = (name || "").trim();
    if (!trimmed) { toast.error("Name cannot be empty."); return; }
    setSavingName(true);
    try {
      const updated = await updateMyProfile(trimmed);
      setProfile(updated);
      setName(updated.name || "");
      onProfileUpdated?.(updated);
      toast.success("Profile updated successfully.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to update profile. Please try again.");
    } finally {
      setSavingName(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setPwError("");
    if (!curPw) { setPwError("Current password is required."); return; }
    if (!newPw) { setPwError("New password is required."); return; }
    if (!confirmPw) { setPwError("Please confirm your new password."); return; }
    if (newPw.length < 8) { setPwError("Password must be at least 8 characters."); return; }
    if (newPw !== confirmPw) { setPwError("New passwords do not match."); return; }
    if (newPw === curPw) { setPwError("New password must be different from current password."); return; }
    setChangingPw(true);
    try {
      await changeMyPassword(curPw, newPw);
      setCurPw(""); setNewPw(""); setConfirmPw("");
      toast.success("Password updated successfully.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Unable to update password. Please try again.";
      setPwError(msg);
    } finally {
      setChangingPw(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
            <User className="w-5 h-5 text-[#025979]" /> My Profile
          </DialogTitle>
          <DialogDescription>View your profile and manage your password.</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Profile */}
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-[#1F2937] block mb-1">Name</label>
              <Input
                data-testid="profile-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-1 gap-3 rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-3">
              <ReadonlyRow icon={Mail} label="Email" value={p.email} testId="profile-email" />
              <ReadonlyRow icon={Shield} label="Role / User Type" value={roleLabel} testId="profile-role" />
              <ReadonlyRow icon={Layers} label="Application Access" value={appAccess} testId="profile-app-access" />
            </div>
            <div className="flex justify-end">
              <Button
                data-testid="profile-save-name-btn"
                onClick={saveName}
                disabled={!nameChanged || savingName}
                className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-sm h-9"
              >
                {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Profile"}
              </Button>
            </div>
          </div>

          {/* SmartEWM User Context */}
          <SmartewmUserContextSection open={open} />

          {/* Change Password */}
          <form onSubmit={submitPassword} className="space-y-3 border-t border-[#E5E7EB] pt-4">
            <p className="text-sm font-semibold text-[#1F2937] flex items-center gap-1.5">
              <Lock className="w-4 h-4 text-[#025979]" /> Change Password
            </p>
            <div>
              <label className="text-xs font-medium text-[#1F2937] block mb-1">Current Password</label>
              <Input
                data-testid="profile-current-password"
                type="password"
                autoComplete="current-password"
                value={curPw}
                onChange={(e) => setCurPw(e.target.value)}
                className="rounded-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#1F2937] block mb-1">New Password</label>
              <Input
                data-testid="profile-new-password"
                type="password"
                autoComplete="new-password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="rounded-sm"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#1F2937] block mb-1">Confirm New Password</label>
              <Input
                data-testid="profile-confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="rounded-sm"
              />
            </div>
            {pwError && <p className="text-xs text-red-500" data-testid="profile-password-error">{pwError}</p>}
            <div className="flex justify-end">
              <Button
                type="submit"
                data-testid="profile-change-password-btn"
                disabled={changingPw}
                className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-sm h-9"
              >
                {changingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Password"}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const NONE = "__none__";

/**
 * SmartEWM User Context — the user's operational context for the SmartEWM
 * Agent. Editable for admins, read-only for other roles. Values inherit from
 * the client/tenant default configuration when no personal override is set.
 */
function SmartewmUserContextSection({ open }) {
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [shiftOptions, setShiftOptions] = useState([]);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    Promise.all([getSmartewmUserContext(), getMySmartewmContext().catch(() => ({ clients: [] }))])
      .then(([uc, runtime]) => {
        if (!active) return;
        setCtx(uc.context || {});
        setCanEdit(!!uc.canEdit);
        setShiftOptions(uc.shiftOptions || []);
        setClients(runtime.clients || []);
      })
      .catch(() => { if (active) setCtx({}); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open]);

  const set = (k) => (v) => setCtx((c) => ({ ...c, [k]: v }));
  const systemsForClient = (cid) =>
    (clients.find((c) => c.clientId === cid)?.systems) || [];

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateSmartewmUserContext({
        defaultClientId: ctx.defaultClientId || null,
        defaultSapSystem: ctx.defaultSapSystem || null,
        defaultWarehouse: ctx.defaultWarehouse || null,
        defaultPlant: ctx.defaultPlant || null,
        defaultShift: ctx.defaultShift || null,
      });
      setCtx(res.context || ctx);
      toast.success("SmartEWM User Context saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to save context.");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3 border-t border-[#E5E7EB] pt-4" data-testid="smartewm-user-context-section">
      <p className="text-sm font-semibold text-[#1F2937] flex items-center gap-1.5">
        <Warehouse className="w-4 h-4 text-[#025979]" /> SmartEWM User Context
      </p>
      <p className="text-[11px] text-[#94A3B8] -mt-1">
        Your operational context for the SmartEWM Agent.
      </p>

      {loading ? (
        <div className="py-4 text-center"><Loader2 className="w-4 h-4 mx-auto animate-spin text-[#025979]" /></div>
      ) : canEdit ? (
        <div className="space-y-3">
          <CtxField icon={Building2} label="Default Business Client">
            <Select value={ctx.defaultClientId || NONE} onValueChange={(v) => set("defaultClientId")(v === NONE ? null : v)}>
              <SelectTrigger className="h-8 text-xs" data-testid="suc-client"><SelectValue placeholder="Select client" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>None</SelectItem>
                {clients.map((c) => <SelectItem key={c.clientId} value={c.clientId}>{c.clientName}</SelectItem>)}
              </SelectContent>
            </Select>
          </CtxField>
          <CtxField icon={Server} label="Default SAP System">
            <Select value={ctx.defaultSapSystem || NONE} onValueChange={(v) => set("defaultSapSystem")(v === NONE ? null : v)}>
              <SelectTrigger className="h-8 text-xs" data-testid="suc-sap-system"><SelectValue placeholder="Select SAP system" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>None</SelectItem>
                {systemsForClient(ctx.defaultClientId).map((s) => (
                  <SelectItem key={s.clientSapId} value={s.clientSapId}>{s.displayName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CtxField>
          <div className="grid grid-cols-2 gap-3">
            <CtxField icon={Warehouse} label="Default Warehouse">
              <Input value={ctx.defaultWarehouse || ""} onChange={(e) => set("defaultWarehouse")(e.target.value)} className="h-8 text-xs" data-testid="suc-warehouse" />
            </CtxField>
            <CtxField icon={Factory} label="Default Plant">
              <Input value={ctx.defaultPlant || ""} onChange={(e) => set("defaultPlant")(e.target.value)} className="h-8 text-xs" data-testid="suc-plant" />
            </CtxField>
          </div>
          <CtxField icon={Clock} label="Default Shift">
            <Select value={ctx.defaultShift || NONE} onValueChange={(v) => set("defaultShift")(v === NONE ? null : v)}>
              <SelectTrigger className="h-8 text-xs" data-testid="suc-shift"><SelectValue placeholder="Select shift" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>None</SelectItem>
                {shiftOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </CtxField>
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-sm h-9" data-testid="suc-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Context"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] p-3">
          <ReadonlyRow icon={Building2} label="Business Client" value={ctx?.defaultClientName || ctx?.defaultClientId} testId="suc-ro-client" />
          <ReadonlyRow icon={Server} label="SAP System" value={ctx?.defaultSapSystemLabel || ctx?.defaultSapSystem} testId="suc-ro-sap-system" />
          <ReadonlyRow icon={Warehouse} label="Warehouse" value={ctx?.defaultWarehouse} testId="suc-ro-warehouse" />
          <ReadonlyRow icon={Factory} label="Plant" value={ctx?.defaultPlant} testId="suc-ro-plant" />
          <ReadonlyRow icon={Clock} label="Shift" value={ctx?.defaultShift} testId="suc-ro-shift" />
        </div>
      )}
    </div>
  );
}

function CtxField({ icon: Icon, label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-semibold flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </span>
      {children}
    </label>
  );
}
